import { Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { cn } from '../../lib/cn';
import type { RouteLoadingScreenProps } from './route-loading-screen.types';

export function RouteLoadingScreen({
  contained = false,
  label = 'Loading page',
  visible = true,
  delayMs = 300,
  minVisibleMs = 400,
  className,
  ...props
}: RouteLoadingScreenProps) {
  const [shown, setShown] = useState(delayMs === 0 && visible);
  const shownAt = useRef<number | null>(shown ? Date.now() : null);

  useEffect(() => {
    if (visible) {
      if (shown) return undefined;
      const timer = window.setTimeout(() => {
        shownAt.current = Date.now();
        setShown(true);
      }, delayMs);
      return () => window.clearTimeout(timer);
    }

    if (!shown) return undefined;
    const elapsed = shownAt.current ? Date.now() - shownAt.current : 0;
    const timer = window.setTimeout(() => setShown(false), Math.max(0, minVisibleMs - elapsed));
    return () => window.clearTimeout(timer);
  }, [delayMs, minVisibleMs, shown, visible]);

  if (!shown) return null;

  return (
    <div
      {...props}
      data-component="route-loading-screen"
      role="status"
      aria-label={label}
      className={cn(
        'flex items-center justify-center bg-[var(--a-page)]',
        contained ? 'min-h-[40vh]' : 'min-h-dvh',
        className,
      )}
    >
      <Loader2
        className="h-5 w-5 animate-spin text-[var(--a-primary)] motion-reduce:animate-none"
        aria-hidden="true"
      />
    </div>
  );
}

RouteLoadingScreen.displayName = 'RouteLoadingScreen';
